import React, { useState } from "react";
import { Check, Filter } from "lucide-react";

interface AdminColumnFilterPopoverProps {
  columnKey: string;
  options: string[];
  label: string;
  selectedValues: Set<string>;
  onToggleValue: (columnKey: string, value: string) => void;
  onSelectAll: (columnKey: string, values: string[]) => void;
  onClear: (columnKey: string) => void;
}

const AdminColumnFilterPopover: React.FC<AdminColumnFilterPopoverProps> = ({
  columnKey,
  options,
  label,
  selectedValues,
  onToggleValue,
  onSelectAll,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const hasFilter = selectedValues.size > 0;

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      setPopoverPosition(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setPopoverPosition(null);
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={handleButtonClick}
        className={`p-0.5 rounded transition-colors ${
          hasFilter
            ? "text-rp-amber"
            : "text-rp-grey/30 hover:text-rp-grey/60"
        }`}
        title={`Filter by ${label}`}
      >
        <Filter className="w-3 h-3" />
      </button>
      {isOpen && popoverPosition && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleClose} />
          <div
            style={{
              position: "fixed",
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
            className="z-50 min-w-[160px] rounded-lg border shadow-xl bg-rp-surface-raised border-rp-separator"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2 border-b border-rp-separator text-xs font-medium text-rp-grey/80">
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold">{label}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => onSelectAll(columnKey, options)}
                    className="px-2 py-0.5 text-xs rounded transition-colors hover:bg-rp-separator text-rp-amber"
                  >
                    All
                  </button>
                  <button
                    onClick={() => onClear(columnKey)}
                    className="px-2 py-0.5 text-xs rounded transition-colors hover:bg-rp-separator text-rp-grey/50 hover:text-rp-grey"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
            <div className="py-1 max-h-60 overflow-y-auto custom-scrollbar">
              {options.map((option) => {
                const displayLabel = option === "" ? "(unset)" : option;
                return (
                  <div
                    key={option}
                    onClick={() => onToggleValue(columnKey, option)}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer transition-colors hover:bg-rp-surface text-rp-grey/70"
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        selectedValues.has(option)
                          ? "bg-rp-amber border-rp-amber"
                          : "border-rp-grey/30"
                      }`}
                    >
                      {selectedValues.has(option) && (
                        <Check
                          className="w-3 h-3 text-black"
                          strokeWidth={3}
                        />
                      )}
                    </div>
                    <span>{displayLabel}</span>
                  </div>
                );
              })}
            </div>
            {selectedValues.size > 0 && (
              <div className="px-3 py-2 border-t border-rp-separator text-xs font-medium text-rp-amber">
                {selectedValues.size} of {options.length} selected
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminColumnFilterPopover;
